import React from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { Box, CircularProgress, Tooltip } from "@mui/material";

import {
  COMPLETED_PROGRESS_STATUS,
  FAILED_DURING_CONVERSION,
  FAILED_PROGRESS_STATUS,
  getManualTaskConfig,
  getTeamsTaskConfig,
  IN_PROGRESS_STATUS,
} from "./BackgroundContainerLogic";
import BackgroundTasks from "./BackgroundTasks";
import { UPLOAD_TASK, UPLOAD_TEAMS_TASK } from "@/context/BackgroundContext";
import { BackgroundTask } from "@/util/interfaces/background/BackgroundTask";
import { BaseTask } from "@/util/interfaces/background/BaseTask";

type BackgroundContentProps = {
  tasks: BackgroundTask[];
  onDismissTask: (_task_id: string) => void;
  callbackRefetch: () => void;
};
const BackgroundContainer: React.FC<BackgroundContentProps> = ({
  tasks,
  onDismissTask,
  callbackRefetch,
}) => {
  const validateItemsOfTask = (task: BackgroundTask) => {
    const { type, tasks } = task;
    return tasks.map((taskItem: BaseTask) => {
      const { file, response } = taskItem;

      if (response) {
        if (type === UPLOAD_TASK) {
          return { ...file, ...getManualTaskConfig(taskItem) };
        }
        if (type === UPLOAD_TEAMS_TASK) {
          return { ...file, ...getTeamsTaskConfig(taskItem) };
        }
      }
      return {
        ...file,
        status: IN_PROGRESS_STATUS,
        text: "Upload in progress",
      };
    });
  };
  const validateTask = (task: BackgroundTask) => {
    //We need to validate
    // Task status (it should return the status, icon and tooltip message)
    // With this, we can define the general task status
    const failedIcon = <Icon icon={"material-symbols:warning"} color="#FF9905" />;
    const progressIcon = <CircularProgress size={12} />;
    const completedIcon = <Icon icon={"carbon:checkmark-filled"} color="green" />;
    const errorIcon = <Icon icon={"material-symbols:warning"} color="red" />;
    let title = `${task.type} (${task.tasks?.length})`;
    let icon = progressIcon;
    const validatedItems = validateItemsOfTask(task);
    const uncompletedTasks = validatedItems.some((vI) => vI.status === IN_PROGRESS_STATUS);
    if (!uncompletedTasks) {
      const successfulItems = validatedItems.filter(
        (item: any) =>
          item.status === COMPLETED_PROGRESS_STATUS || item.status === FAILED_DURING_CONVERSION
      );
      if (successfulItems.length > 0) {
        callbackRefetch();
      }
      if (
        validatedItems.some(
          (item: any) =>
            item.status === FAILED_PROGRESS_STATUS || item.status === FAILED_DURING_CONVERSION
        )
      ) {
        icon = failedIcon;
      } else {
        icon = completedIcon;
      }
      title = `Files uploaded (${successfulItems.length}/${validatedItems.length})`;
    }
    return {
      completed: !uncompletedTasks,
      titleIcon: icon,
      titleText: title,
      items: validatedItems.map((item: any) => {
        const { status, name, text } = item;
        let icon = progressIcon;
        if (status === COMPLETED_PROGRESS_STATUS) {
          icon = completedIcon;
        } else if (status === FAILED_DURING_CONVERSION) {
          icon = failedIcon;
        } else if (status === FAILED_PROGRESS_STATUS) {
          icon = errorIcon;
        }
        return (
          <div className="flex gap-5 align-items-center" key={name}>
            <Tooltip arrow title={text}>
              {icon}
            </Tooltip>
            <Tooltip arrow title={name}>
              <span className="text-overflow-ellipsis" style={{ maxWidth: "80%", width: "80%" }}>
                {name}
              </span>
            </Tooltip>
          </div>
        );
      }),
    };
  };
  return (
    <Box
      sx={{
        padding: "5px",
        display: "flex",
        flexDirection: "column-reverse",
        rowGap: "5px",
        position: "absolute",
        right: "2%",
        bottom: "10%",
        width: "15vw",
        maxHeight: "50vh",
        overflowY: "auto",
      }}
    >
      {tasks.map((task) => {
        const { id } = task;
        const { titleText, titleIcon, items, completed } = validateTask(task);
        return (
          <BackgroundTasks
            key={id}
            id={id}
            title={
              <div className="flex gap-5 align-items-center">
                {titleIcon}
                <div className="flex space-between" style={{ width: "100%", marginRight: "10px" }}>
                  <div className="flex gap-5 align-items-center">
                    <span>{titleText}</span>
                  </div>
                  {completed && (
                    <button
                      title="Dismiss"
                      className="btn-default btn-default-style"
                      onClick={(e) => {
                        e.preventDefault();
                        onDismissTask(id);
                      }}
                    >
                      <Icon icon={"material-symbols:close-rounded"} />
                    </button>
                  )}
                </div>
              </div>
            }
          >
            {items}
          </BackgroundTasks>
        );
      })}
    </Box>
  );
};

export default BackgroundContainer;
